import { useEffect, useState } from "react";
import { Banknote, QrCode, CheckCircle2, Printer, FileText } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/pos/lib/api";
import { rm, roundCash } from "@/pos/lib/format";
import { getPrinter } from "@/pos/lib/printer";
import { printSale } from "@/pos/lib/print";
import { Bluetooth } from "lucide-react";

function Done({ sale, onNew, onVoid }) {
  return (
    <div className="space-y-5 text-center" data-testid="checkout-success">
      <CheckCircle2 size={56} className="mx-auto text-green-500" />
      <div>
        <div className="font-heading text-2xl font-bold">Jualan Berjaya</div>
        <div className="text-sm text-slate-500" data-testid="sale-number">{sale.number}</div>
      </div>
      <div className="rounded-xl bg-slate-50 p-4 text-left font-mono text-sm">
        <div className="flex justify-between"><span>Jumlah</span><b>{rm(sale.total)}</b></div>
        {sale.payment_method === "tunai" && <div className="flex justify-between"><span>Baki</span><b className="text-green-700" data-testid="sale-change">{rm(sale.change)}</b></div>}
      </div>
      <button className="btn-primary h-11 w-full !bg-[#2A0707]" data-testid="pos-bt-print-btn" onClick={() => printSale(sale)}><Bluetooth size={16} /> Cetak Resit (Bluetooth)</button>
      <div className="grid grid-cols-2 gap-2">
        <button className="btn-outline" data-testid="pos-print-receipt-btn" onClick={() => window.open(`/pos/cetak/resit/${sale.id}`, "_blank")}><Printer size={16} /> Paparan Resit</button>
        <button className="btn-outline" data-testid="pos-print-invoice-btn" onClick={() => window.open(`/pos/cetak/invois/${sale.id}`, "_blank")}><FileText size={16} /> Invois A4</button>
      </div>
      <button className="btn-primary h-12 w-full" onClick={onNew} data-testid="new-sale-btn">Jualan Baru</button>
      <button className="w-full text-xs font-medium text-rose-600 hover:underline" onClick={onVoid} data-testid="checkout-void-btn">Batal Resit Ini</button>
    </div>
  );
}

export default function CheckoutDialog({ open, onClose, cart, subtotal, onDone, orderIds }) {
  const [method, setMethod] = useState("tunai");
  const [received, setReceived] = useState("");
  const [discount, setDiscount] = useState("");
  const [customerId, setCustomerId] = useState("umum");
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sale, setSale] = useState(null);

  useEffect(() => {
    if (open) {
      setSale(null); setReceived(""); setDiscount(""); setMethod("tunai"); setCustomerId("umum");
      api.get("/customers").then((r) => setCustomers(r.data));
    }
  }, [open]);

  const net = Math.max(subtotal - (parseFloat(discount) || 0), 0);
  const total = method === "tunai" ? roundCash(net) : Math.round(net * 100) / 100;
  const rec = parseFloat(received) || 0;
  const change = rec - total;

  const submit = async () => {
    setBusy(true);
    try {
      const pay = {
        discount: parseFloat(discount) || 0, payment_method: method,
        amount_received: method === "tunai" ? rec || total : total,
        customer_id: customerId === "umum" ? null : customerId,
      };
      const r = orderIds
        ? await api.post("/orders/checkout", { ...pay, order_ids: orderIds })
        : await api.post("/sales", { ...pay, items: cart.map((c) => ({ product_id: c.product.id, option: c.option || "", qty: c.qty, note: c.note || "" })) });
      setSale(r.data);
      onDone();
      if (getPrinter().autoPaid) printSale(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const voidSale = async () => {
    if (!window.confirm(`Batalkan resit ${sale.number}?`)) return;
    try { await api.post(`/sales/${sale.id}/void`); toast.success("Resit dibatalkan"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="checkout-dialog">
        {sale ? <Done sale={sale} onNew={onClose} onVoid={voidSale} /> : (
          <>
            <DialogHeader><DialogTitle className="font-heading text-xl">Bayaran</DialogTitle></DialogHeader>
            <div className="hero-green rounded-2xl p-5 text-white">
              <div className="text-xs uppercase tracking-wider text-red-50/90">Jumlah Perlu Dibayar</div>
              <div className="mt-1 font-mono text-4xl font-bold" data-testid="checkout-total">{rm(total)}</div>
              {method === "tunai" && total !== Math.round(net * 100) / 100 && <div className="mt-1 text-xs text-red-50/90">Termasuk pembundaran {rm(total - net)}</div>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[["tunai", "Tunai", Banknote, "cash"], ["qr", "QR DuitNow", QrCode, "qr"]].map(([m, l, I, tid]) => (
                <button key={m} onClick={() => setMethod(m)} data-testid={`pos-payment-method-${tid}`}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 py-3 font-semibold transition-colors ${method === m ? "border-red-600 bg-red-50 text-red-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <I size={18} /> {l}
                </button>
              ))}
            </div>
            {method === "tunai" && (
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Wang Diterima</label>
                <Input type="number" inputMode="decimal" value={received} onChange={(e) => setReceived(e.target.value)} placeholder={total.toFixed(2)} className="h-12 font-mono text-lg" data-testid="cash-received-input" />
                <div className="flex flex-wrap gap-2">
                  {[["Tepat", total], ["RM10", 10], ["RM20", 20], ["RM50", 50], ["RM100", 100]].filter(([, v]) => v >= total).map(([l, v]) => (
                    <button key={l} onClick={() => setReceived(String(v))} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium hover:bg-slate-200" data-testid={`cash-quick-${l}`}>{l}</button>
                  ))}
                </div>
                {rec > 0 && <div className={`font-mono text-sm font-bold ${change < 0 ? "text-rose-600" : "text-green-700"}`} data-testid="checkout-change">{change < 0 ? "Tidak cukup " : "Baki "}{rm(Math.abs(change))}</div>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Diskaun (RM)</label>
                <Input type="number" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" data-testid="discount-input" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Pelanggan</label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger data-testid="checkout-customer-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="umum">Pelanggan Umum</SelectItem>
                    {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <button onClick={submit} disabled={busy || (method === "tunai" && rec > 0 && change < 0)} data-testid="pos-checkout-confirm-btn" className="btn-primary h-12 w-full text-base disabled:opacity-40">
              {busy ? "Memproses..." : `Sahkan Bayaran ${rm(total)}`}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
